import { fetchConfig } from '@theme/utilities';
import {
  CartLinesUpdateEvent,
  CartErrorEvent
} from '@shopify/events';


const RECENT_KEY = 'viewedProducts';

const MAX_RECENT = 4;

const MOBILE_QUERY = '(max-width: 749px)';


/* =========================================================
   GET RECENT PRODUCT IDS
   ========================================================= */

function getStoredIds() {

  try {

    const value =
      JSON.parse(
        localStorage.getItem(RECENT_KEY) || '[]'
      );

    return Array.isArray(value)
      ? value
          .map(String)
          .slice(0, MAX_RECENT)
      : [];

  }

  catch (error) {

    return [];

  }

}


/* =========================================================
   ESCAPE
   ========================================================= */

function escapeHTML(value = '') {

  return String(value)

    .replaceAll('&', '&amp;')

    .replaceAll('<', '&lt;')

    .replaceAll('>', '&gt;')

    .replaceAll('"', '&quot;')

    .replaceAll("'", '&#039;');

}


/* =========================================================
   MONEY
   ========================================================= */

function money(cents) {

  const currency =
    window.Shopify?.currency?.active
    ||
    'INR';


  try {

    return new Intl.NumberFormat(
      'en-IN',
      {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0
      }
    )
    .format(
      (Number(cents) || 0) / 100
    );

  }

  catch (error) {

    return (
      '₹'
      +
      Math.round(
        (Number(cents) || 0) / 100
      )
    );

  }

}


/* =========================================================
   PRODUCT HANDLE
   ========================================================= */

function productHandle(product) {

  if (product?.handle) {

    return product.handle;

  }


  const match =
    String(product?.url || '')
    .match(
      /\/products\/([^/?#]+)/
    );


  return match
    ? match[1]
    : '';

}


/* =========================================================
   LOAD RECENT PRODUCTS
   Uses Horizon's existing recently viewed product IDs
   ========================================================= */

async function fetchRecentlyViewedProducts(ids) {

  if (!ids.length) {

    return [];

  }


  const suggestRoute =
    window.Theme?.routes?.predictive_search_url
    ||
    '/search/suggest.json';


  const url =
    new URL(
      suggestRoute,
      window.location.origin
    );


  url.searchParams.set(
    'q',
    ids
      .map(
        id => `id:${id}`
      )
      .join(' OR ')
  );


  url.searchParams.set(
    'resources[type]',
    'product'
  );


  url.searchParams.set(
    'resources[limit]',
    String(
      Math.max(
        ids.length,
        MAX_RECENT
      )
    )
  );


  url.searchParams.set(
    'resources[options][unavailable_products]',
    'last'
  );


  const response =
    await fetch(
      url.toString(),
      {
        headers: {
          Accept: 'application/json'
        },

        credentials: 'same-origin'
      }
    );


  if (!response.ok) {

    throw new Error(
      `Recently viewed search failed: ${response.status}`
    );

  }


  const json =
    await response.json();


  const found =
    json?.resources?.results?.products
    ||
    [];


  const byId =
    new Map(
      found.map(
        product => [
          String(product.id),
          product
        ]
      )
    );


  const ordered =
    ids

      .map(
        id =>
          byId.get(
            String(id)
          )
      )

      .filter(Boolean);


  const detailed =
    await Promise.all(

      ordered.map(

        async product => {

          const handle =
            productHandle(product);


          if (!handle) {

            return null;

          }


          try {

            const productResponse =
              await fetch(
                `/products/${encodeURIComponent(handle)}.js`,
                {
                  headers: {
                    Accept: 'application/json'
                  },

                  credentials: 'same-origin'
                }
              );


            if (!productResponse.ok) {

              return null;

            }


            return await productResponse.json();

          }

          catch (error) {

            return null;

          }

        }

      )

    );


  return detailed.filter(Boolean);

}


/* =========================================================
   WEIGHT
   ========================================================= */

function weightText(variant) {

  const weight =
    Number(
      variant?.weight || 0
    );


  const unit =
    variant?.weight_unit
    ||
    'g';


  return weight > 0
    ? `${weight} ${unit}`
    : '';

}


/* =========================================================
   CREATE PRODUCT CARD
   ========================================================= */

function buildRecentCard(product) {

  const variant =

    product.variants?.find(
      item => item.available
    )

    ||

    product.variants?.[0];


  if (!variant) {

    return '';

  }


  const image =

    product.featured_image

    ||

    product.images?.[0]

    ||

    '';


  const weight =
    weightText(variant);


  const meta =

    [
      weight,
      money(variant.price)
    ]

    .filter(Boolean)

    .join(' · ');


  const isAvailable =
    Boolean(
      variant.available
    );


  return `

    <article
      class="foxena-recent__card"
      data-product-id="${escapeHTML(product.id)}"
    >

      <a
        href="${escapeHTML(product.url)}"
        tabindex="-1"
        aria-hidden="true"
      >

        <img
          class="foxena-recent__image"
          src="${escapeHTML(image)}"
          alt="${escapeHTML(product.title)}"
          width="112"
          height="112"
          loading="lazy"
        >

      </a>


      <div class="foxena-recent__info">

        <a
          class="foxena-recent__name"
          href="${escapeHTML(product.url)}"
        >
          ${escapeHTML(product.title)}
        </a>


        <div class="foxena-recent__meta">
          ${escapeHTML(meta)}
        </div>


        <button
          class="foxena-recent__add"
          type="button"
          data-foxena-recent-add
          data-variant-id="${escapeHTML(variant.id)}"
          ${isAvailable ? '' : 'disabled'}
        >

          ${
            isAvailable
              ? '+ Add'
              : 'Sold out'
          }

        </button>

      </div>

    </article>

  `;

}


/* =========================================================
   MOBILE SLIDER CLEANUP
   ========================================================= */

function stopMobileSlider(root) {

  const state =
    root._foxenaSliderState;


  if (!state) {

    return;

  }


  state.stop?.();

  state.cleanup?.();


  root._foxenaSliderState =
    null;

}


/* =========================================================
   PHONE ONLY
   INFINITE RECENTLY VIEWED SLIDER
   ========================================================= */

function setupMobileSlider(root) {

  stopMobileSlider(root);


  if (
    !window
      .matchMedia(MOBILE_QUERY)
      .matches
  ) {

    return;

  }


  if (
    root.dataset.context
    !==
    'page'
  ) {

    return;

  }


  if (
    window
      .matchMedia(
        '(prefers-reduced-motion: reduce)'
      )
      .matches
  ) {

    return;

  }


  const viewport =
    root.querySelector(
      '[data-foxena-recent-viewport]'
    );


  const track =
    root.querySelector(
      '[data-foxena-recent-track]'
    );


  if (
    !viewport
    ||
    !track
  ) {

    return;

  }


  const originals =
    Array.from(
      track.children
    );


  const count =
    originals.length;


  if (count < 2) {

    return;

  }


  /* clone cards for infinite loop */

  originals.forEach(

    card =>

      track.appendChild(
        card.cloneNode(true)
      )

  );


  let index = 0;

  let timer = null;

  let locked = false;

  let startX = null;



  function stepSize() {

    const card =
      track.querySelector(
        '.foxena-recent__card'
      );


    if (!card) {

      return 0;

    }


    const gap =
      parseFloat(
        window
          .getComputedStyle(track)
          .gap
      )
      ||
      7;


    return (
      card
        .getBoundingClientRect()
        .width
      +
      gap
    );

  }



  function move(animated = true) {

    track.style.transition =

      animated

      ?

      'transform .48s cubic-bezier(.4,0,.2,1)'

      :

      'none';


    track.style.transform =

      `translateX(${
        -index * stepSize()
      }px)`;

  }



  function next() {

    if (locked) {

      return;

    }


    locked =
      true;


    index +=
      1;


    move(true);


    window.setTimeout(

      () => {

        if (
          index >= count
        ) {

          index =
            0;


          move(false);

        }


        locked =
          false;

      },

      500

    );

  }



  function previous() {

    if (locked) {

      return;

    }


    if (
      index === 0
    ) {

      index =
        count;


      move(false);

    }


    window.requestAnimationFrame(

      () => {

        locked =
          true;


        index -=
          1;


        move(true);


        window.setTimeout(

          () => {

            locked =
              false;

          },

          500

        );

      }

    );

  }



  function stop() {

    if (timer) {

      window.clearInterval(
        timer
      );

    }


    timer =
      null;

  }



  function start() {

    stop();


    timer =
      window.setInterval(
        next,
        2400
      );

  }



  function onTouchStart(event) {

    stop();


    startX =
      event
        .touches?.[0]
        ?.clientX
      ??
      null;

  }



  function onTouchEnd(event) {

    if (
      startX !== null
    ) {

      const endX =
        event
          .changedTouches?.[0]
          ?.clientX
        ??
        startX;


      const diff =
        startX
        -
        endX;


      if (
        Math.abs(diff)
        >
        35
      ) {

        diff > 0
          ?
          next()
          :
          previous();

      }

    }


    startX =
      null;


    start();

  }



  function onResize() {

    move(false);

  }



  viewport.addEventListener(
    'touchstart',
    onTouchStart,
    {
      passive: true
    }
  );


  viewport.addEventListener(
    'touchend',
    onTouchEnd,
    {
      passive: true
    }
  );


  window.addEventListener(
    'resize',
    onResize
  );


  root._foxenaSliderState = {

    stop,


    cleanup() {

      viewport.removeEventListener(
        'touchstart',
        onTouchStart
      );


      viewport.removeEventListener(
        'touchend',
        onTouchEnd
      );


      window.removeEventListener(
        'resize',
        onResize
      );

    }

  };


  move(false);

  start();

}


/* =========================================================
   INITIALIZE RECENT SECTION
   ========================================================= */

async function initRecentRoot(root) {

  if (
    !(root instanceof HTMLElement)
    ||
    root.dataset.foxenaRecentReady
    ===
    'true'
  ) {

    return;

  }


  root.dataset.foxenaRecentReady =
    'true';


  const storedIds =
    getStoredIds();


  const cartIds =
    new Set(

      String(
        root.dataset.cartProductIds
        ||
        ''
      )

      .split(',')

      .map(
        id => id.trim()
      )

      .filter(Boolean)

    );


  const idsToShow =

    storedIds.filter(
      id =>
        !cartIds.has(id)
    );


  if (!idsToShow.length) {

    return;

  }


  try {

    const products =
      await fetchRecentlyViewedProducts(
        idsToShow
      );


    const filtered =

      products.filter(
        product =>
          !cartIds.has(
            String(product.id)
          )
      );


    const limit =

      root.dataset.context
      ===
      'drawer'

      ?

      1

      :

      3;


    const visibleProducts =
      filtered.slice(
        0,
        limit
      );


    if (
      !visibleProducts.length
    ) {

      return;

    }


    const track =
      root.querySelector(
        '[data-foxena-recent-track]'
      );


    if (!track) {

      return;

    }


    track.innerHTML =

      visibleProducts

        .map(
          buildRecentCard
        )

        .join('');


    root.hidden =
      false;


    setupMobileSlider(root);

  }

  catch (error) {

    console.warn(
      '[FOXENA cart] Recently viewed products could not be loaded.',
      error
    );

  }

}


/* =========================================================
   INIT ALL
   ========================================================= */

function initRecentRoots(
  scope = document
) {

  scope
    .querySelectorAll?.(
      '[data-foxena-recent]'
    )
    .forEach(
      initRecentRoot
    );

}


/* =========================================================
   AJAX ADD TO CART
   ========================================================= */

async function addRecentVariant(button) {

  const variantId =
    button.dataset.variantId;


  if (
    !variantId
    ||
    button.disabled
  ) {

    return;

  }


  button.disabled =
    true;


  const defaultText =
    button.textContent;


  button.textContent =
    'Adding…';



  const sectionIds =

    Array.from(
      document.querySelectorAll(
        'cart-items-component'
      )
    )

    .map(
      component =>
        component.dataset.sectionId
    )

    .filter(Boolean);



  const formData =
    new FormData();


  formData.set(
    'id',
    variantId
  );


  formData.set(
    'quantity',
    '1'
  );


  if (
    sectionIds.length
  ) {

    formData.set(

      'sections',

      [
        ...new Set(sectionIds)
      ]
      .join(',')

    );

  }


  formData.set(
    'sections_url',
    window.location.pathname
  );



  const deferred =
    CartLinesUpdateEvent.createPromise();



  button.dispatchEvent(

    new CartLinesUpdateEvent({

      action: 'add',

      context: 'cart',

      lines: [
        {
          merchandiseId: variantId,
          quantity: 1
        }
      ],

      promise:
        deferred.promise

    })

  );



  try {

    const config =
      fetchConfig(
        'javascript',
        {
          body: formData
        }
      );


    const response =
      await fetch(

        window.Theme?.routes?.cart_add_url
        ||
        '/cart/add.js',

        {

          ...config,

          headers: {

            ...config.headers,

            Accept: 'text/html'

          }

        }

      );


    const payload =
      await response.json();



    if (
      !response.ok
      ||
      payload.status
    ) {

      throw new Error(

        payload.description

        ||

        payload.message

        ||

        'Unable to add product'

      );

    }



    const cartResponse =
      await fetch(

        `${
          window.Theme?.routes?.cart_url
          ||
          '/cart'
        }.json`,

        {

          headers: {
            Accept:
              'application/json'
          },

          credentials:
            'same-origin'

        }

      );



    if (
      !cartResponse.ok
    ) {

      throw new Error(
        'Unable to refresh cart'
      );

    }



    const cart =
      await cartResponse.json();



    deferred.resolve({

      cart:
        CartLinesUpdateEvent
          .createCartFromAjaxResponse(
            cart
          ),

      detail: {

        items:
          cart.items,

        source:
          'foxena-cart-recently-viewed',

        itemCount:
          cart.item_count,

        sections:
          payload.sections,

        didError:
          false

      }

    });



    button.textContent =
      'Added ✓';

  }

  catch (error) {

    deferred.reject(
      error
    );


    button.dispatchEvent(

      new CartErrorEvent({

        error:
          error?.message
          ||
          'Unable to add product',

        code:
          'INVALID'

      })

    );


    button.disabled =
      false;


    button.textContent =
      defaultText;


    console.error(
      '[FOXENA cart] Add failed:',
      error
    );

  }

}


/* =========================================================
   ADD BUTTON
   ========================================================= */

document.addEventListener(

  'click',

  event => {

    const button =

      event.target instanceof Element

      ?

      event.target.closest(
        '[data-foxena-recent-add]'
      )

      :

      null;


    if (
      !(
        button
        instanceof
        HTMLButtonElement
      )
    ) {

      return;

    }


    event.preventDefault();


    addRecentVariant(
      button
    );

  }

);


/* =========================================================
   RE-INITIALIZE AFTER HORIZON MORPHS CART
   ========================================================= */

const observer =
  new MutationObserver(

    mutations => {

      for (
        const mutation
        of
        mutations
      ) {

        for (
          const node
          of
          mutation.addedNodes
        ) {

          if (
            !(
              node
              instanceof
              Element
            )
          ) {

            continue;

          }


          if (
            node.matches(
              '[data-foxena-recent]'
            )
          ) {

            initRecentRoot(
              node
            );

          }


          initRecentRoots(
            node
          );

        }

      }

    }

  );


observer.observe(

  document.documentElement,

  {
    childList: true,
    subtree: true
  }

);


document.addEventListener(

  'DOMContentLoaded',

  () =>
    initRecentRoots()

);


initRecentRoots();